import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { LABELS, PageHead, ScopeFilters, days, inp, qs, usePeople } from "@/lib/crm";
import { money } from "@/lib/pricing";
import { promptSheet } from "@/components/ui/action-sheet";

function Card({ c, onMove }) {
  return (
    <div data-testid={`pipeline-card-${c.number}`} className="space-y-2 rounded-xl border border-slate-800 bg-white p-3 shadow-sm transition-transform hover:-translate-y-0.5">
      <Link to={`/quotes/${c.id}`} className="block">
        <p className="text-sm font-semibold text-slate-100">{c.client_name}</p>
        <p className="text-xs text-slate-500">{c.project_name || "—"} · {c.number} v{c.version}</p>
      </Link>
      <div className="flex items-center justify-between text-xs">
        <span className="font-mono-num font-semibold text-slate-100">{money(c.total)}</span>
        <span className="text-slate-500">{c.rep_name} · {days(c.days_in_stage)}</span>
      </div>
      <select data-testid={`pipeline-card-stage-${c.number}`} value={c.stage} onChange={(e) => onMove(c, e.target.value)} className={`${inp} h-8 text-xs`}>
        {Object.keys(LABELS).filter((s) => s !== "paid" || c.stage === "paid").map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
      </select>
    </div>
  );
}

export default function Pipeline() {
  const people = usePeople();
  const [f, setF] = useState({});
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/pipeline?${qs(f)}`).then((r) => setD(r.data)).catch((e) => toast.error(errorMessage(e))), [f]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const move = async (c, stage) => {
    const back = stage !== "declined" && d.stages.indexOf(stage) < d.stages.indexOf(c.stage);
    const reason = back || c.stage === "declined"
      ? await promptSheet({ title: `Move ${c.number} to ${LABELS[stage]}`, message: "Reason for moving back", placeholder: "Reason", confirm: "Move", required: back })
      : "";
    if (reason === null) return;
    try { await api.post(`/quotes/${c.id}/stage`, { stage, reason: reason || "" }); toast.success(`${c.number} → ${LABELS[stage]}`); load(); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-8" data-testid="pipeline-page">
      <PageHead kicker="CRM" title="Pipeline" sub="Every estimate by stage. Change a stage from the card." />
      <ScopeFilters people={people} f={f} setF={setF} prefix="pipeline" />
      <div className="flex gap-4 overflow-x-auto pb-4">
        {d?.stages.map((s) => {
          const cards = d.cards.filter((c) => c.stage === s);
          return (
            <div key={s} data-testid={`pipeline-column-${s}`} className="w-64 shrink-0 space-y-3 rounded-2xl border border-slate-800 bg-[#EAF3FF]/60 p-3">
              <div className="flex items-baseline justify-between"><p className="text-xs font-semibold uppercase tracking-wider text-slate-300">{LABELS[s]}</p>
                <p className="text-[11px] text-slate-500" data-testid={`pipeline-column-total-${s}`}>{cards.length} · {money(cards.reduce((a, c) => a + c.total, 0))}</p></div>
              {cards.map((c) => <Card key={c.id} c={c} onMove={move} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
